



















































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ServiceWorkerManager from '@/lib/util/ServiceWorkerManager';
import UpdateTasks from '@/lib/UpdateTasks';
import Lockr from 'lockr';
import { wlPath } from '@/lib/Whitelabel';

type OutcomeState = ('failed'|'succeeded');
type ExecutionState = ('not-started'|'in-progress'|'failed'|'succeeded');
interface NameState {
  pathname: string;
  state: ExecutionState;
}
interface TasksWithState {
  updates: NameState[];
  deletes: NameState[];
}

@Component
export default class AppUpdater extends Vue {

  // reactive properties
  isStarted: boolean = false;
  isUpdateCacheAutomatically = false;
  isUpdateCacheAutomaticallyInitialState = false;
  isUpdateCacheAutomaticallyAtShipTime: boolean | null = null;
  tasks: TasksWithState = {
    updates: [],
    deletes: [],
  };

  // injected properties
  @Prop({ required: true }) updateTasks!: UpdateTasks;

  // non-reactive properties

  // watches
  @Watch('isUpdateCacheAutomatically')
  onChangeIsUpdateCacheAutomatically(newVal: boolean) {
    Lockr.set('kb_isUpdateCacheAutomatically', newVal);
  }
  @Watch('isUpdateDoneAndSuccessful')
  onChangeIsUpdateDoneAndSuccessful(isDoneAndSuccessful: boolean) {
    if ( isDoneAndSuccessful ) {
      this.isUpdateCacheAutomaticallyAtShipTime = this.isUpdateCacheAutomatically;
    }
    if ( this.isUpdateCacheAutomatically && isDoneAndSuccessful ) {
      setTimeout(() => {
        this.btnStartKiosk();
      }, 5000);
    }
  }

  // lifecycle
  created() {
    this.isUpdateCacheAutomatically = Lockr.get('kb_isUpdateCacheAutomatically', false);
    this.isUpdateCacheAutomaticallyInitialState = this.isUpdateCacheAutomatically;
  }
  mounted() {
    if ( this.updateTasks ) {
      //console.log("cache update tasks: ", this.updateTasks);
      this.tasks.updates = this.updateTasks.updates.map( (task) => ({ pathname: task, state: 'not-started' }));
      this.tasks.deletes = this.updateTasks.deletes.map( (task) => ({ pathname: task, state: 'not-started' }));
    }
    if ( this.isUpdateCacheAutomatically ) {
      this.startUpdateTasks();
    }
  }

  // computed
  get currentDate(): string {
    return new Date().toString();
  }
  get whitelabelPath(): string {
    return wlPath;
  }
  get updatesTotal(): number {
    return this.tasks.updates.length;
  }
  get updatesSucceeded(): number {
    return this.countTasksHavingState(this.tasks.updates, 'succeeded');
  }
  get updatesFailed(): number {
    return this.countTasksHavingState(this.tasks.updates, 'failed');
  }
  get deletesTotal(): number {
    return this.tasks.deletes.length;
  }
  get deletesSucceeded(): number {
    return this.countTasksHavingState(this.tasks.deletes, 'succeeded');
  }
  get deletesFailed(): number {
    return this.countTasksHavingState(this.tasks.deletes, 'failed');
  }
  get isTasksDone(): boolean {
    const isUpdatesDone = this.updatesTotal === ( this.updatesSucceeded + this.updatesFailed );
    const isDeletesDone = this.deletesTotal === ( this.deletesSucceeded + this.deletesFailed );
    return isUpdatesDone && isDeletesDone;
  }
  get isUpdateDoneAndSuccessful(): boolean {
    return this.isTasksDone && ( 0 === this.updatesFailed ) && ( 0 === this.deletesFailed );
  }
  get isUpdateDoneButHasFailures(): boolean {
    return this.isTasksDone && (( this.updatesFailed > 0 ) || ( this.deletesFailed > 0 ));
  }
  get isShowStartDownloadArea(): boolean {
    const wouldShowDownloadButtonArea = !this.isUpdateCacheAutomaticallyInitialState;
    return !this.isStarted && wouldShowDownloadButtonArea;
  }
  get isYouMayLaunchMessage(): boolean {
    const isAutomatic = null === this.isUpdateCacheAutomaticallyAtShipTime ?
      this.isUpdateCacheAutomatically : this.isUpdateCacheAutomaticallyAtShipTime;
    return this.isUpdateDoneAndSuccessful && !isAutomatic;
  }

  // methods
  executionStates(nameStateList: NameState[]): ExecutionState[] {
    return nameStateList.map((task: NameState) => task.state);
  }
  countTasksHavingState(nameStateList: NameState[], havingState: ExecutionState): number {
    return this.executionStates(nameStateList).reduce((sum: number, state: ExecutionState) => {
      return (state === havingState) ? sum + 1 : sum; }, 0
    );
  }

  btnStartKiosk() {
    console.log('start kiosk');
    this.$emit('on-start-kiosk');
  }
  btnReload() {
    window.location.replace('/');
  }

  startUpdateTasks() {
    const manager = ServiceWorkerManager.instance();
    manager.whenReady().then(() => {
      this.isStarted = true;
      const controller = manager.activeWorker;
      if ( controller ) {
        this.doAllCacheUpdateTasks(controller, this.tasks);
      }
      else {
        console.log("expected active worker to be non-null");
        this.btnStartKiosk();
      }
    });
  }

  statusIconClass(task: NameState) {
    const isSpinner = ( task.state === 'in-progress' );
    return {
      'mdi-loading': isSpinner,
      'animate-spin': isSpinner,
      'mdi-check': task.state === 'succeeded',
      'mdi-alert-outline': task.state === 'failed',
      'mdi-clock-outline': task.state === 'not-started',
    };
  }

  setTaskStatus(videoPathname: string, action: 'update' | 'delete', state: ExecutionState) {
    if ( 'update' === action ) {
      const item = this.tasks.updates.find((o) => o.pathname === videoPathname);
      if ( item ) {
        item.state = state;
      }
    }
    else if ( 'delete' === action ) {
      const item = this.tasks.deletes.find((o) => o.pathname === videoPathname);
      if ( item ) {
        item.state = state;
      }
    }
  }

  doAllCacheUpdateTasks(controller: ServiceWorker, tasks: TasksWithState) {
    tasks.updates.forEach((task: NameState) => {
      this.setTaskStatus(task.pathname, 'update', 'in-progress');
      this.doCacheUpdateTask(controller, task.pathname, 'update')
      .then((outcome: OutcomeState) => {
        this.setTaskStatus(task.pathname, 'update', outcome);
      });
    });
    tasks.deletes.forEach((task: NameState) => {
      this.setTaskStatus(task.pathname, 'delete', 'in-progress');
      this.doCacheUpdateTask(controller, task.pathname, 'delete')
      .then((outcome: OutcomeState) => {
        this.setTaskStatus(task.pathname, 'delete', outcome);
      });
    });
  }

  doCacheUpdateTask(controller: ServiceWorker, videoPathname: string, action: 'update' | 'delete'): Promise<OutcomeState> {
    return new Promise<OutcomeState>((resolve, reject) => {
      const channel = new MessageChannel();
      channel.port1.onmessage = (event) => {
        //console.log("event.data=", event.data);
        if ( !event.data.isSuccess ) {
          console.log('   <-- fail ' + action + ' ' + videoPathname);
          if ( event.data.error ) {
            console.log('       error: ', event.data.error);
          }
          resolve('failed');
        }
        else {
          console.log('   <-- done ' + action + ' ' + videoPathname);
          resolve('succeeded');
        }
      };
      console.log('  `--> cache ' + action + ' ' + videoPathname);
      controller.postMessage({
        name: 'doCacheUpdateTask',
        videoPathname,
        action,
      }, [channel.port2]);
    });
  }

}

